import { isCourseFamilyAvailableIn } from "@/config/courseStateMapping";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CentresGrid from '@/components/CentresGrid';
import { useLocation } from '@/contexts/LocationContext';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { BookOpen, Users, Calendar, MapPin, Star, CheckCircle, ArrowRight, Play, Award, Target, Clock, GraduationCap, MessageCircle, Phone, Mail, Trophy, Sparkles, Crown, DollarSign, PartyPopper, Zap, Monitor, ChevronDown } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import LocationPopup from '@/components/LocationPopup';
import { useLocationPopup } from '@/hooks/useLocationPopup';
import StructuredDataMarkup from '@/components/StructuredDataMarkup';

import SEOFAQSection from '@/components/SEOFAQSection';
import { Helmet } from 'react-helmet-async';
import { getRandomizedTestimonials } from '@/utils/testimonialAggregator';
import OptimizedImage from '@/components/OptimizedImage';
import { useState, useEffect, useRef } from 'react';
import BabelStyleHero from '@/components/BabelStyleHero';
import NewCentreLaunch from '@/components/centres/NewCentreLaunch';
import StateBlogCarousel from '@/components/StateBlogCarousel';
import { useInView } from '@/hooks/useInView';
import { WhyChooseMobileHeader, WhyChooseMobileStats, WhyChooseMobileTmrw, WhyChooseMobileFeatures, WhyChooseMobileCta, WhyChooseDesktopHeader, WhyChooseDesktopTmrwCard, WhyChooseDesktopStatsCard, WhyChooseDesktopFeatureCard, WhyChooseDesktopCta } from '@/components/WhyChooseAnimatedSections';
import { ExpertCoursesHeader, MobileCoursesList, DesktopCoursesCarousel } from '@/components/ExpertCoursesAnimatedSections';
import type { HomepageProgram } from '@/components/ExpertCoursesAnimatedSections';
import { coursesForState, type AustralianState, type CourseDefinition } from '@/data/courses/courses';
const Index = () => {
  const {
    currentStateConfig,
    selectedState,
    loading
  } = useLocation();
  const {
    isPopupOpen,
    closePopup
  } = useLocationPopup();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Load the form embed script for SCOPE registration
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      try {
        const existingScript = document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]');
        if (existingScript && existingScript.parentNode) {
          existingScript.parentNode.removeChild(existingScript);
        }
      } catch (error) {
        // Silently handle the case where script removal fails
        console.warn('Script cleanup failed:', error);
      }
    };
  }, []);
  const stats = [{
    label: 'Students Helped',
    value: '2,500+'
  }, {
    label: 'Expert Tutors',
    value: '50+'
  }];
  const courseIcons: Record<CourseDefinition['iconName'], React.ElementType> = {
    GraduationCap,
    Target,
    BookOpen,
    Zap,
    Code: BookOpen,
    Trophy,
    Brain: Target,
    Clock,
    ClipboardCheck: CheckCircle,
  };
  const stateCode = selectedState.toUpperCase() as AustralianState;
  const allPrograms: HomepageProgram[] = coursesForState(stateCode)
    .filter((course) => course.homepageCategory)
    .map((course) => ({
      title: course.name,
      description: course.description,
      icon: courseIcons[course.iconName],
      features: course.features,
      href: course.href,
      badge: course.badges?.[0] === 'most_popular'
        ? 'Most Popular'
        : course.badges?.[0] === 'popular'
          ? 'Popular'
          : undefined,
      category: course.homepageCategory ?? 'foundations',
    }));
  // Get randomized testimonials from individual center pages
  const testimonials = getRandomizedTestimonials(selectedState, 6);

  // Get the correct state name for the banner
  const getStateName = () => {
    if (selectedState && currentStateConfig) {
      return currentStateConfig.name;
    }
    return 'Australia';
  };

  // Get the state-specific hero image
  const getHeroImage = () => {
    if (selectedState === 'VIC') {
      return '/lovable-uploads/vic-hero-new-2025-optimized.webp';
    }
    if (selectedState === 'NSW') {
      return '/lovable-uploads/nsw-hero-new-2025.jpg';
    }
    if (currentStateConfig && currentStateConfig.heroImage) {
      return currentStateConfig.heroImage;
    }
    return '/lovable-uploads/default-hero-optimized.webp'; // Default fallback
  };
  return <div className="min-h-screen bg-white overflow-x-hidden">
      <Helmet>
        <title>Schoolola - Kindergarten, Prep or Pre-primary to Year 8 Tutoring in Australia</title>
        <meta name="description" content="Australian tutoring centres specialising in OC, Selective, VSEC, NAPLAN and scholarship preparation, plus foundational Kindergarten, Prep or Pre-primary to Year 8 and Year 7-8 Maths and English. Centres across NSW, Victoria and WA, with online tutoring Australia-wide." />
        <meta name="keywords" content="tutoring australia, in-centre tutoring, online tutoring, kindergarten to year 8, prep to year 8, pre-primary to year 8, OC preparation, selective school coaching, scholarship exam preparation, NAPLAN preparation, VSEC tutoring, Year 7 8 Maths English, NSW tutoring, Victoria tutoring, WA tutoring, TMRW methodology" />
        <meta property="og:title" content="Schoolola - Kindergarten, Prep or Pre-primary to Year 8 Tutoring in Australia" />
        <meta property="og:description" content="Australian tutoring centres specialising in OC, Selective, VSEC, NAPLAN and scholarship preparation, plus foundational Kindergarten, Prep or Pre-primary to Year 8 and Year 7-8 Maths and English. Centres across NSW, Victoria and WA, with online tutoring Australia-wide." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://schoolola.com.au/" />
        <meta property="og:image" content="https://schoolola.com.au/lovable-uploads/og-schoolola-default-v2.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Schoolola - Kindergarten, Prep or Pre-primary to Year 8 Tutoring in Australia" />
        <meta name="twitter:description" content="Australian tutoring centres specialising in OC, Selective, VSEC, NAPLAN and scholarship preparation, plus foundational Kindergarten, Prep or Pre-primary to Year 8 and Year 7-8 Maths and English. Centres across NSW, Victoria and WA, with online tutoring Australia-wide." />
        <meta name="twitter:image" content="https://schoolola.com.au/lovable-uploads/og-schoolola-default-v2.jpg" />
        <meta name="geo.region" content="AU" />
        <meta name="geo.placename" content="Australia" />
      </Helmet>
      <StructuredDataMarkup type="homepage" data={{}} />
      {/* SchoololaRatingSchema removed pending real review data source */}
      <Header />
      
      {/* Babbel-Style Hero Section */}
      <BabelStyleHero />

      <NewCentreLaunch surface="homepage" regionLabel="Melbourne's west" />




      {/* State-specific Centres Section with Enhanced Carousel */}
      {/* Why Choose Schoolola Section - Mobile Version */}
      <section className="md:hidden py-16 bg-gradient-to-b from-background via-muted/30 to-background">
        <div className="container mx-auto px-4">
          {/* Header */}
          <WhyChooseMobileHeader />

          {/* Stats Row - Horizontal Scroll */}
          <WhyChooseMobileStats />

          {/* TMRW Card */}
          <WhyChooseMobileTmrw />

          {/* Feature Grid - 2x2 */}
          <WhyChooseMobileFeatures />

          {/* CTA */}
          <WhyChooseMobileCta />
        </div>
      </section>

      {/* Why Choose Schoolola Section - Modern Redesign */}
      <section className="hidden md:block pt-24 pb-12 bg-gradient-to-b from-background via-muted/30 to-background overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Header with accent line */}
          <WhyChooseDesktopHeader />

          {/* SEO Content - Hidden from users but available to search engines */}
          <div className="sr-only">
            <h3>Specialised Primary Education Focus (Kindergarten, Prep or Pre-primary to Year 8)</h3>
            <p>
              Unlike generic tutoring services, Schoolola specialises exclusively in primary education from Kindergarten, Prep or Pre-primary to Year 8. 
              This focused approach allows us to deeply understand the unique learning needs, developmental stages, and curriculum 
              requirements for young learners. Our teachers are specifically trained in early childhood education and primary 
              teaching methodologies, ensuring age-appropriate instruction that builds confidence alongside academic skills.
              We recognise that primary school years are crucial foundation-building years. Students who develop strong 
              mathematical reasoning, reading comprehension, and critical thinking skills during these formative years are 
              better prepared for the academic challenges ahead, including OC tests, selective school entrance exams, and 
              high school mathematics and English courses.
            </p>
            
            <h3>Proven TMRW™ Methodology for Academic Excellence</h3>
            <p>
              Our exclusive TMRW™ (Thinking, Method, Reasoning, Writing) methodology has been developed through years of 
              educational research and real-world classroom application. This systematic approach doesn't just teach students 
              what to learn, but how to think critically, solve problems methodically, and communicate their understanding clearly.
              The TMRW™ methodology integrates seamlessly with NSW and Victorian curricula whilst providing additional depth 
              and challenge. Students learn to approach mathematical problems with logical reasoning, tackle reading comprehension 
              with analytical skills, and express their ideas through structured writing. This comprehensive skill development 
              prepares students not only for immediate academic success but for lifelong learning.
            </p>
            
            <h3>Qualified Teachers with Primary Education Expertise</h3>
            <p>
              Every Schoolola teacher holds qualified teaching credentials with specific experience in primary education. 
              Our rigorous recruitment process ensures that our educators understand child development, learning psychology, 
              and effective teaching strategies for students aged 5-12. Many of our teachers have additional qualifications 
              in special education, gifted education, or specific subject specialisations.
              Beyond qualifications, our teachers undergo continuous professional development in the TMRW™ methodology, 
              ensuring consistent, high-quality instruction across all our centres. They are trained to identify individual 
              learning styles, adapt teaching methods accordingly, and provide both academic support and confidence building.
            </p>
            
            <h3>Small Class Sizes for Personalised Attention</h3>
            <p>
              With a maximum of 8 students per class, Schoolola maintains an optimal learning environment where every student 
              receives individual attention. This small class structure allows teachers to monitor each student's progress closely, 
              address specific learning challenges immediately, and provide targeted support where needed. Students benefit from 
              peer interaction and collaborative learning whilst still receiving personalised instruction.
              Small classes also mean that shy students feel more comfortable participating, confident students can be appropriately 
              challenged, and students with different learning speeds can all progress at their optimal pace. Teachers can implement 
              differentiated instruction strategies, ensuring that each student's unique needs are met within the group setting.
            </p>
            
            <h3>Comprehensive Programme Options: In-Centre and Online Tutoring</h3>
            <p>
              Schoolola offers both premium in-centre tutoring and interactive online tutoring options, providing flexibility 
              for modern families. Our in-centre programmes provide the benefits of face-to-face instruction, peer interaction, 
              and a dedicated learning environment. For families preferring convenience or living in remote areas, our online 
              tutoring maintains the same high standards with interactive technology and real-time teacher feedback.
              Whether students choose in-centre or online tutoring, they receive the same comprehensive curriculum, TMRW™ methodology, 
              and qualified teacher instruction. Our hybrid approach means families can even combine both options based on their 
              schedule, ensuring consistent learning regardless of circumstances.
            </p>
            
            <h3>Proven Results in OC and Selective School Preparation</h3>
            <p>
              Schoolola has established an outstanding track record in preparing students for Opportunity Class (OC) tests 
              and selective school entrance exams. Our systematic approach combines curriculum mastery with specific test-taking 
              strategies, ensuring students are academically prepared and confident on exam day. We provide regular practice tests, 
              detailed performance analysis, and targeted remediation to address any knowledge gaps.
              Beyond test preparation, our programmes develop the advanced thinking skills and academic foundation that ensure 
              students thrive once they gain entry to OC or selective programmes. We prepare students not just to pass the test, 
              but to excel in their chosen academic pathway.
            </p>
          </div>

          {/* Modern Bento Grid Layout */}
          <div className="max-w-7xl mx-auto">
            {/* Top Row - Large Feature Cards */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <WhyChooseDesktopTmrwCard />
              <WhyChooseDesktopStatsCard />
            </div>

            {/* Bottom Row - Feature Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <WhyChooseDesktopFeatureCard icon={GraduationCap} title="Expert Teachers" description="Qualified educators with advanced degrees and specialised primary education training." iconColor="text-primary" bgFrom="from-primary/10" bgTo="to-accent/10" delay={300} />
              <WhyChooseDesktopFeatureCard icon={Users} title="Small Classes" description="Maximum 8 students ensures personalised attention and optimal learning." iconColor="text-accent" bgFrom="from-accent/10" bgTo="to-primary/10" delay={400} />
              <WhyChooseDesktopFeatureCard icon={Monitor} title="Flexible Options" description="In-centre and online tutoring to suit your family's lifestyle." iconColor="text-primary" bgFrom="from-primary/10" bgTo="to-accent/10" delay={500} />
              <WhyChooseDesktopFeatureCard icon={Award} title="Primary & Early Secondary Specialists" description="Specialist in primary and early secondary tuition, from Kindergarten, Prep or Pre-primary through to Year 8." iconColor="text-accent" bgFrom="from-accent/10" bgTo="to-primary/10" delay={600} />
            </div>

            {/* CTA Row */}
            <WhyChooseDesktopCta />
          </div>
        </div>
      </section>

      {/* Featured Programmes Section */}
      <section className="py-12 bg-background relative overflow-hidden md:py-0">
        <div className="container mx-auto px-4">
          <ExpertCoursesHeader subtitle={selectedState && currentStateConfig ? `Comprehensive tutoring courses designed specifically for ${currentStateConfig.name} students` : 'Comprehensive tutoring courses designed to help every student achieve their academic goals'} />
          <MobileCoursesList programs={allPrograms} />
          <DesktopCoursesCarousel programs={allPrograms} selectedState={selectedState} />
        </div>
      </section>


      {/* Success Stories Section - Modernized */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-background via-muted/20 to-background relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-accent/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-2 text-sm font-medium backdrop-blur-sm">
              <Star className="h-4 w-4 mr-2 fill-primary" />
              Trusted by 2,000+ Families
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground">
              Success Stories from Families
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {['QLD', 'WA', 'SA', 'NT', 'ACT'].includes(selectedState) ? 'Real results and reviews from families across Australia' : `Real results from families across ${selectedState && currentStateConfig ? currentStateConfig.name : 'Australia'}`}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <Carousel className="w-full" plugins={[Autoplay({
            delay: 4000
          }) as any]} opts={{
            align: "start",
            loop: true
          }}>
              <CarouselContent className="-ml-2 md:-ml-4 py-4">
                {testimonials.map((testimonial, index) => <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                    <Card className="border border-border/50 bg-card/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 h-full group">
                      <CardContent className="p-6 md:p-8 space-y-5 h-full flex flex-col relative">
                        {/* Quote icon */}
                        <div className="absolute -top-3 -left-1 text-primary/20 text-6xl font-serif leading-none select-none">"</div>
                        
                        {/* Stars */}
                        <div className="flex space-x-1 pt-4">
                          {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                        </div>
                        
                        {/* Quote content */}
                        <blockquote className="text-foreground/80 leading-relaxed flex-grow text-sm md:text-base">
                          {testimonial.content}
                        </blockquote>
                        
                        {/* Author info */}
                        <div className="pt-4 border-t border-border/50 mt-auto">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary font-semibold text-sm">
                              {testimonial.name.charAt(0)}
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-foreground text-sm">{testimonial.name}</div>
                              {testimonial.center && <div className="text-xs text-muted-foreground">
                                {testimonial.center} Centre
                              </div>}
                            </div>
                          </div>
                          {testimonial.result && <div className="mt-3">
                            <span className="text-xs text-emerald-600 font-medium bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {testimonial.result}
                            </span>
                          </div>}
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="-left-4 md:-left-12 lg:-left-16 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background" />
              <CarouselNext className="-right-4 md:-right-12 lg:-right-16 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Awards Section - NSW Only */}
      {selectedState === 'NSW' && <section className="py-12 md:py-16 bg-gradient-to-b from-background via-muted/30 to-background relative overflow-hidden">
          {/* Modern background elements */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-accent/5 rounded-full blur-[100px]"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              {/* Header Section */}
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6 border border-primary/20">
                  <Trophy className="h-4 w-4" />
                  <span>Award-Winning Excellence</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4 tracking-tight">
                  Recognised Industry Leader
                </h2>
                <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                  Celebrating our commitment to educational excellence
                </p>
              </div>

              {/* Modern Award Card */}
              <div className="relative">
                {/* Glow effect behind card */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 rounded-3xl blur-2xl opacity-50 transform scale-95"></div>
                
                <Card className="relative overflow-hidden bg-card/95 backdrop-blur-xl border border-border/50 shadow-2xl rounded-3xl">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {/* Content Section */}
                      <div className="p-8 md:p-12 lg:p-14 space-y-8">
                        {/* Award Badges */}
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg">
                            <Award className="h-4 w-4" />
                            2025 Winner
                          </span>
                          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-muted text-muted-foreground border border-border/50">
                            Quality Business Awards
                          </span>
                        </div>

                        {/* Main Content */}
                        <div className="space-y-4">
                          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                            Best Tutoring Service
                          </h3>
                          <p className="text-xl md:text-2xl text-primary font-medium">
                            City of Parramatta
                          </p>
                          <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-lg">
                            Recognised for our unwavering commitment to educational excellence, innovative teaching methodologies, and outstanding student outcomes.
                          </p>
                        </div>

                        {/* Features Grid */}
                        <div className="grid grid-cols-2 gap-4">
                          {[{
                        label: 'Excellence in Education',
                        color: 'bg-primary'
                      }, {
                        label: 'Student Success Focus',
                        color: 'bg-accent'
                      }, {
                        label: 'Innovative Methods',
                        color: 'bg-primary'
                      }, {
                        label: 'Community Impact',
                        color: 'bg-accent'
                      }].map((feature, index) => <div key={index} className="flex items-center gap-3 group/feature">
                              <div className={`h-2.5 w-2.5 ${feature.color} rounded-full group-hover/feature:scale-125 transition-transform`}></div>
                              <span className="text-sm text-muted-foreground group-hover/feature:text-foreground transition-colors">{feature.label}</span>
                            </div>)}
                        </div>

                        {/* CTA Button */}
                        <div className="pt-4">
                          <Link to="/awards">
                            <Button size="lg" className="group/btn bg-foreground hover:bg-foreground/90 text-background rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 px-8 h-14 text-base">
                              <Trophy className="mr-2 h-5 w-5 group-hover/btn:rotate-12 transition-transform duration-300" />
                              View Award Details
                              <ArrowRight className="ml-2 h-5 w-5 group-hover/btn:translate-x-1 transition-transform duration-300" />
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Visual Section */}
                      <div className="relative bg-gradient-to-br from-muted/50 to-muted/30 flex items-center justify-center p-8 md:p-12 lg:p-14 min-h-[300px] lg:min-h-0">
                        {/* Decorative rings */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-64 h-64 border border-primary/10 rounded-full"></div>
                          <div className="absolute w-48 h-48 border border-primary/15 rounded-full"></div>
                          <div className="absolute w-32 h-32 border border-primary/20 rounded-full"></div>
                        </div>
                        
                        {/* Award Image */}
                        <div className="relative z-10">
                          <div className="relative group/award">
                            {/* Glow behind image */}
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-accent/30 rounded-3xl blur-xl opacity-60 group-hover/award:opacity-80 transition-opacity"></div>
                            
                            <div className="relative bg-background/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-border/50 group-hover/award:scale-105 transition-all duration-500">
                              <Link to="/awards" className="block">
                                <OptimizedImage src="/lovable-uploads/award-image-optimized.webp" alt="Quality Business Awards 2025 - Best Tutoring Service in Parramatta" className="w-36 h-36 md:w-44 md:h-44 object-contain mx-auto" width={176} height={176} />
                              </Link>
                            </div>
                            
                            {/* Floating accent dots */}
                            <div className="absolute -top-3 -right-3 w-6 h-6 bg-primary rounded-full shadow-lg"></div>
                            <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-accent rounded-full shadow-lg"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>}



      {/* State Blog Carousel */}
      <StateBlogCarousel 
        stateCode={selectedState} 
        stateName={selectedState === 'VIC' ? 'Victoria' : selectedState === 'NSW' ? 'New South Wales' : currentStateConfig?.name || 'Australia'} 
      />

      {/* SEO FAQ Section */}
      <SEOFAQSection title="Frequently Asked Questions About Our Tutoring Programs" centreSpecific={false} faqs={[{
      question: "What age groups do you teach?",
      answer: "We specialise in students from Kindergarten, Prep or Pre-primary through to Year 8. Our primary programmes cover Foundations, OC, Selective, VSEC, NAPLAN, and scholarship preparation. For Year 7 and Year 8 students, we offer Maths and English foundations to support the transition to high school.",
      isKeyword: true
    }, {
      question: "Do you offer both in-centre and online tutoring?",
      answer: "Yes, we offer both premium in-centre tutoring and interactive online tutoring options. Students can choose between face-to-face instruction at our centres or flexible online sessions, both delivered by qualified teachers.",
      isKeyword: true
    }, {
      question: "What subjects do you cover for primary students?",
      answer: "We offer comprehensive tutoring in Mathematics, English, Reading Comprehension, Writing, and General Ability. Our programmes include OC preparation, Selective School coaching, NAPLAN preparation, and Foundations programmes. For Year 7 and Year 8 students, we also offer Maths and English foundations programmes.",
      isKeyword: true
    }, {
      question: "Where are your tutoring centres located?",
      answer: "We have tutoring centres across NSW and Victoria. We also offer online tutoring to students anywhere in Australia, providing flexibility for families.",
      isKeyword: true
    }, {
      question: "What is your TMRW™ methodology?",
      answer: "Our TMRW™ methodology is a proven approach that combines targeted practice, strategic thinking, and comprehensive exam preparation. It focuses on building both foundational skills and advanced problem-solving abilities for primary students.",
      isKeyword: true
    }, {
      question: "How small are your class sizes?",
      answer: "We maintain small class sizes with a maximum of 8 students per class. This ensures personalised attention and allows our qualified teachers to focus on each student's individual learning needs.",
      isKeyword: true
    }, {
      question: "Are your teachers qualified for primary education?",
      answer: "Yes, all our teachers are qualified NSW, VIC and WA curriculum teachers with specific experience in primary and early secondary education (Kindergarten, Prep or Pre-primary to Year 8). They are trained in our TMRW™ methodology and specialise in working with students from Kindergarten, Prep or Pre-primary to Year 8.",
      isKeyword: true
    }, {
      question: "How do I book an assessment?",
      answer: "You can book a free diagnostic assessment through our online booking system or by contacting your nearest centre. This helps us understand your child's current level and recommend the most suitable program.",
      isKeyword: true
    }]} />


      {/* CTA Section with Enhanced Glassmorphism */}
      <section className="py-12 md:py-16 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              Ready to Transform Your Child's Academic Journey?
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              Join thousands of families who have seen remarkable improvements in their children's academic performance with Schoolola Tutoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              {['QLD', 'WA', 'SA', 'NT', 'ACT', 'TAS'].includes(selectedState) ? <Button size="lg" disabled className="w-full sm:w-auto bg-gray-400 text-lg px-8 py-4 rounded-2xl min-h-[52px] font-semibold shadow-xl cursor-not-allowed opacity-50">
                  Book Your Free Assessment Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button> : <Link to="/book-assessment">
                  <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-lg px-8 py-4 rounded-2xl min-h-[52px] font-semibold shadow-xl">
                    Book Your Free Assessment Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>}
              <Link to="/contact">
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 min-h-[52px] font-semibold shadow-xl">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>




      <Footer />
      
      {/* Location Popup */}
      <LocationPopup isOpen={isPopupOpen} onClose={closePopup} />
    </div>;
};
export default Index;